import React, { useCallback, useEffect, useState } from 'react';
import { ObjectTypeFieldDataType } from '../../../../common/src/domain/settings/objectType/ObjectTypeFieldDataType';
import { OpenApiRouteResultType } from '../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetch } from '../../store/openApiFetch';
import { useCurrentAppContext } from '../../utils/useCurrentAppContext';
import { EditPanelFilterSelect, EditPanelInput, EditPanelSection } from '../elements';
import { EditPanelInputNumber } from '../utils/editPanel/EditPanelInputNumber';
import { EditPanelTrueFalseUndefined } from '../utils/editPanel/EditPanelTrueFalseUndefined';

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

export type FieldToFilter = {
    id: string;
    value: string | boolean | number | undefined | null;
    dataType: ObjectTypeFieldDataType;
    shouldSubmit: boolean;
};

type Props = { name: string; objectTypeId: string; onChange(fieldToFilter: FieldToFilter | undefined, name: string): void };

export function ObjectTypeFieldSelect({ objectTypeId, onChange, name }: Props) {
    const [objectTypeFields, setObjectTypeFields] = useState<OpenApiRouteResultType<'/api/objectTypes/{objectTypeId}/objectTypeFields', 'get'>>({
        items: [],
    });
    const [selectedObjectTypeField, setSelectedObjectTypeField] = useState<ArrayElement<typeof objectTypeFields.items> | undefined>();
    const [filterValue, setFilterValue] = useState<string | boolean | number | undefined | null>();

    const appContext = useCurrentAppContext();
    const translate = useTranslate();

    const onFilterValueChanged = useCallback(
        (objectTypeField: typeof selectedObjectTypeField, value: string | boolean | number | undefined | null, shouldSubmit: boolean) => {
            setFilterValue(value);
            if (objectTypeField?.id) {
                onChange({ id: objectTypeField?.id, value, dataType: objectTypeField?.dataType, shouldSubmit }, name);
            }
        },
        [onChange, name],
    );

    const selectObjectTypeField = useCallback(
        (objectTypeFieldId: string) => {
            const selectedField = objectTypeFields.items.find((field) => field.id === objectTypeFieldId);
            setSelectedObjectTypeField(selectedField);
            if (filterValue !== undefined) {
                onFilterValueChanged(selectedField, undefined, true);
            }
        },
        [onFilterValueChanged, objectTypeFields, filterValue],
    );

    useEffect(() => {
        async function load() {
            const fetchedFields = await openApiFetch(
                `/api/objectTypes/{objectTypeId}/objectTypeFields?clientCompanyId=${appContext.companyId}` as '/api/objectTypes/{objectTypeId}/objectTypeFields',
                'get',
                { objectTypeId },
                undefined,
            );
            setObjectTypeFields(fetchedFields);
        }

        void load();

        setSelectedObjectTypeField(undefined);
        setFilterValue(undefined);
    }, [objectTypeId, appContext.companyId]);

    if (objectTypeFields.items.length === 0) {
        return null;
    }

    return (
        <>
            <EditPanelSection>
                <EditPanelFilterSelect
                    name="selectedObjectTypeField"
                    value={selectedObjectTypeField?.id}
                    handleChange={selectObjectTypeField}
                    placeholder={translate('common.selectItem', translate('objectTypeField.objectTypeField'))}
                    options={objectTypeFields.items.map((item) => ({ text: item.name, value: item.id }))}
                />
            </EditPanelSection>
            {selectedObjectTypeField?.dataType === ObjectTypeFieldDataType.String && (
                <EditPanelSection>
                    <EditPanelInput
                        name="objectTypeFieldValue"
                        value={filterValue}
                        handleChange={(value) => onFilterValueChanged(selectedObjectTypeField, value, false)}
                    />
                </EditPanelSection>
            )}
            {selectedObjectTypeField?.dataType === ObjectTypeFieldDataType.Number && (
                <EditPanelSection>
                    <EditPanelInputNumber
                        name="objectTypeFieldValue"
                        value={filterValue as number}
                        onChange={(value) => onFilterValueChanged(selectedObjectTypeField, value, false)}
                    />
                </EditPanelSection>
            )}

            {selectedObjectTypeField?.dataType === ObjectTypeFieldDataType.Boolean && (
                <EditPanelSection>
                    <EditPanelTrueFalseUndefined
                        name="objectTypeFieldValue"
                        value={filterValue as boolean}
                        onChange={(value) => onFilterValueChanged(selectedObjectTypeField, value, true)}
                        size="small"
                    />
                </EditPanelSection>
            )}

            {selectedObjectTypeField?.dataType === ObjectTypeFieldDataType.List && (
                <EditPanelSection>
                    <EditPanelFilterSelect
                        name="objectTypeFieldValue"
                        value={selectedObjectTypeField}
                        handleChange={(value) => onFilterValueChanged(selectedObjectTypeField, value, false)}
                        placeholder={translate('common.selectItem', selectedObjectTypeField.name)}
                        options={selectedObjectTypeField.listOptions.map((item) => ({ text: item.value, value: item.value }))}
                    />
                </EditPanelSection>
            )}
        </>
    );
}
